import { PAYMENT_TYPE, PAYMENT_TYPE_DISPLAY, PAYMENT_TYPE_ID, PAYMENT_TYPE_ID_DISPLAY } from '../constants'
import { bankDetailsJsonToString, cardDetailsJsonToString } from './index'

export function join(array = [], joiner = ' ') {
  return array.filter(Boolean).join(joiner)
}

export const camelToTitleCase = (string) =>
  string
    // insert a space before all caps
    .replace(/([A-Z])/g, ' $1')
    // uppercase the first character
    .replace(/^./, (str) => str.toUpperCase())

export const titleCase = (string) => {
  if (!string) return null
  const lowerCaseString = string.toLowerCase()
  return lowerCaseString.charAt(0).toUpperCase() + lowerCaseString.slice(1)
}

/* duplicated in api/src/pdf/tax-receipt-pdf.service.ts */
export function formatContributionDescription(contribution) {
  const paymentMethod = contribution?.paymentMethod
  const details = paymentMethod?.details
  const paymentType = contribution?.paymentType ?? paymentMethod?.paymentType

  // Handle numeric payment types (from database)
  if (typeof paymentType === 'number') {
    const paymentTypeDisplay = PAYMENT_TYPE_ID_DISPLAY[paymentType] || paymentType

    // For e-transfer and Apple Pay, use description if available, otherwise payment type
    if (paymentType === PAYMENT_TYPE_ID.eft || paymentType === PAYMENT_TYPE_ID.applePay) {
      return contribution.description || paymentTypeDisplay
    }

    // For card and bank transfer, include details if available
    let paymentDetails = ''
    if (paymentType === PAYMENT_TYPE_ID.card && details) {
      paymentDetails = cardDetailsJsonToString(details)
    } else if (paymentType === PAYMENT_TYPE_ID.bankTransfer && details) {
      paymentDetails = bankDetailsJsonToString(details)
    }

    return join([paymentTypeDisplay, paymentDetails, contribution.description], ': ')
  }

  // Handle string payment types (existing logic for backwards compatibility)
  let paymentDetails = ''
  switch (paymentType) {
    case PAYMENT_TYPE.card:
      paymentDetails = details ? cardDetailsJsonToString(details) : ''
      break

    case PAYMENT_TYPE.eft:
      return contribution.description || PAYMENT_TYPE_DISPLAY[PAYMENT_TYPE.eft]
    case PAYMENT_TYPE.bankTransfer:
      paymentDetails = details ? bankDetailsJsonToString(details) : ''
      break
    case PAYMENT_TYPE.applePay:
      return contribution.description || PAYMENT_TYPE_DISPLAY[PAYMENT_TYPE.applePay]
    default:
  }

  const paymentTypeDisplay = PAYMENT_TYPE_DISPLAY[paymentType] || paymentType

  return join([paymentTypeDisplay, paymentDetails, contribution.description], ': ')
}

export const formatRecurrentGrantRequestDescription = (recurrentGrantRequest) => {
  const { grantNote, charity: { accountName } = {} } = recurrentGrantRequest
  return join([accountName, grantNote], ': ')
}

export const userName = (userProfile, fundProfile = null, appendString = '', userOrg = null) => {
  const fundName = fundProfile?.portalName || fundProfile?.craRecognisedName
  if (fundName) return join([fundName, appendString], ' ')

  if (userOrg?.isOrganization && userOrg.corporateName) {
    return join([userOrg.corporateName, appendString], ' ')
  }

  if (!userProfile) return null
  const { firstName, middleInitials, lastName } = userProfile
  return join([firstName, middleInitials, lastName, appendString], ' ')
}

export const formatDafTransferDescription = (dafTransfer, { type }) => {
  const { initiatingUser, initiatingFund, receivingUser, receivingFund, sentTo } = dafTransfer
  const prefix = type === 'initiated' ? 'To' : 'From'
  const name =
    type === 'initiated'
      ? userName(receivingUser?.profile, receivingFund?.fundProfile, '', receivingUser) ?? sentTo
      : userName(initiatingUser?.profile, initiatingFund?.fundProfile, '', initiatingUser) ?? 'Unknown'
  return join([prefix, name], ' ')
}

// Formats the description only if it's for a grant request
export const grantRequestValueFormatter = (descriptionValue, isGrantRequest) => {
  if (isGrantRequest) {
    const [charity, description, processingNote] = descriptionValue
    const formattedDescription = join([charity?.accountName, description], ': ')
    return join([formattedDescription, processingNote], ', ')
  }

  return descriptionValue
}

// Shared display-name derivation for an advisor portal (AdvisorPortalEntity
// shape: { id, portalName, user: { email, profile: { firstName, lastName } } }).
// Prefers the advisor's real name, falls back to their portal name, falls
// back to their email, so the UI never shows a blank or raw id. Used by both
// the admin Investment Portfolios screen and the donor Investment Account tab.
export function formatAdvisorName(advisorPortal) {
  if (!advisorPortal) return null
  const firstName = advisorPortal.user?.profile?.firstName
  const lastName = advisorPortal.user?.profile?.lastName
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim()
  return fullName || advisorPortal.portalName || advisorPortal.user?.email || null
}

// <INVESTMENT ACCOUNT NUMBER> (<FIRM> - <ADVISOR>) -- the firm/advisor part
// drops whichever of the two the portfolio doesn't have (firm is optional on
// a portfolio), and the parentheses with it if neither exists. The one label
// every screen uses for an Investment Account, admin and donor alike.
export function formatInvestmentAccountLabel(portfolio) {
  const detail = [portfolio.financialFirm?.name, formatAdvisorName(portfolio.financialAdvisor)]
    .filter(Boolean)
    .join(' - ')
  return detail ? `${portfolio.custodialNumber} (${detail})` : portfolio.custodialNumber
}
